import React, { useState, useEffect } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";

import {
  LayoutDashboard,
  Tags,
  Package,
  Image,
  ShoppingCart,
  TicketPercent,
  QrCode,
  Mail,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Truck,
  Settings,
  CreditCard,
} from "lucide-react";

const VanyaLayout = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const navigate = useNavigate();
  const location = useLocation();

  // --------------------------------------------------
  // RESPONSIVE SCREEN DETECTION
  // --------------------------------------------------
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      if (!mobile) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // --------------------------------------------------
  // LOGOUT
  // --------------------------------------------------
  const handleLogout = () => {
    localStorage.removeItem("admin");
    localStorage.removeItem("admin_custom_credentials");
    alert("Logged out successfully!");
    navigate("/login");
  };

  // --------------------------------------------------
  // NAVIGATION ITEMS
  // --------------------------------------------------
  const navItems = [
    {
      name: "Dashboard",
      path: "/admin/dashboard",
      icon: LayoutDashboard,
    },
    {
      name: "Manage Categories",
      path: "/admin/categories",
      icon: Tags,
    },
    {
      name: "Manage Products",
      path: "/admin/products",
      icon: Package,
    },
    {
      name: "Banner Management",
      path: "/admin/banner",
      icon: Image,
    },
    {
      name: "Orders",
      path: "/admin/orders",
      icon: ShoppingCart,
    },
    {
      name: "Razorpay Collections",
      path: "/admin/razorpay-collections",
      icon: CreditCard,
    },
    {
      name: "Coupons",
      path: "/admin/coupons",
      icon: TicketPercent,
    },
    {
      name: "QR Code Settings",
      path: "/admin/onlinepaymentssettings",
      icon: QrCode,
    },
    {
      name: "Shipping Settings",
      path: "/admin/shipping",
      icon: Truck,
    },
    {
      name: "Contact",
      path: "/admin/contact",
      icon: Mail,
    },
    {
      name: "Admin Settings",
      path: "/admin/settings",
      icon: Settings,
    },
  ];

  // --------------------------------------------------
  // ACTIVE LINK
  // --------------------------------------------------
  const isActive = (path) => {
    if (path === "/admin") {
      return location.pathname === "/admin";
    }

    return location.pathname.startsWith(path);
  };

  const getPageTitle = () => {
    const item = navItems.find((n) => location.pathname.startsWith(n.path));
    if (item) return item.name;
    if (location.pathname === "/admin/dashboard") return "Dashboard Overview";
    return "Admin Portal";
  };

  // --------------------------------------------------
  // CLOSE SIDEBAR ON MOBILE
  // --------------------------------------------------
  const handleNavigation = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  return (
    <div style={styles.adminWrapper}>

      {/* =====================================================
          MOBILE OVERLAY
      ===================================================== */}
      {isMobile && isSidebarOpen && (
        <div
          style={styles.overlay}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* =====================================================
          SIDEBAR
      ===================================================== */}
      <aside
        style={{
          ...styles.sidebar,
          left: isMobile
            ? isSidebarOpen
              ? "0"
              : "-280px"
            : "0",
        }}
      >

        {/* LOGO */}
        <div style={styles.logoSection}>
          <div style={styles.logoImgWrapper}>
            <img
              src="/vanyalogo.png"
              alt="Vanya Collections"
              style={styles.logoImg}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <div style={styles.logo}>
              Vanya Collections
            </div>

            <div style={styles.logoSub}>
              ADMIN PANEL
            </div>
          </div>

          {/* MOBILE CLOSE */}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(false)}
              style={styles.closeBtn}
            >
              <X size={22} />
            </button>
          )}
        </div>

        {/* DIVIDER */}
        <div style={styles.divider} />

        {/* NAVIGATION */}
        <nav style={styles.nav}>

          <div style={styles.menuLabel}>
            MANAGEMENT
          </div>

          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavigation}
                style={{
                  ...styles.navItem,
                  ...(active ? styles.activeNavItem : {}),
                }}
              >

                {/* ACTIVE INDICATOR */}
                {active && (
                  <span style={styles.activeIndicator} />
                )}

                <div
                  style={{
                    ...styles.navIcon,
                    ...(active
                      ? styles.activeNavIcon
                      : {}),
                  }}
                >
                  <Icon
                    size={19}
                    strokeWidth={active ? 2.4 : 2}
                  />
                </div>

                <span style={{
                  ...styles.navText,
                  fontWeight: active ? "600" : "500",
                  color: active ? "#ffffff" : "rgba(255,255,255,0.85)",
                }}>
                  {item.name}
                </span>

                {active && (
                  <ChevronRight
                    size={16}
                    style={styles.arrow}
                  />
                )}
              </Link>
            );
          })}

        </nav>

        {/* =====================================================
            SIDEBAR BOTTOM
        ===================================================== */}
        <div style={styles.sidebarBottom}>

          <div style={styles.bottomDivider} />

          <button
            onClick={() => {
              handleLogout();

              if (isMobile) {
                setSidebarOpen(false);
              }
            }}
            style={styles.logoutBtn}
          >
            <div style={styles.logoutIcon}>
              <LogOut size={19} />
            </div>

            <span>
              Logout
            </span>
          </button>

          <div style={styles.version}>
            Vanya Admin • v1.0
          </div>

        </div>

      </aside>

      {/* =====================================================
          MAIN CONTENT
      ===================================================== */}
      <main
        style={{
          ...styles.main,
          marginLeft: !isMobile ? "260px" : "0",
        }}
      >

        {/* =====================================================
            TOP HEADER
        ===================================================== */}
        <header
          className="vanya-top-header"
          style={{
            ...styles.topHeader,
            padding: isMobile ? "0 12px" : "0 25px",
            height: isMobile ? "56px" : "64px",
          }}
        >

          <div
            style={{
              ...styles.headerLeft,
              flex: 1,
              minWidth: 0,
              gap: isMobile ? "10px" : "15px",
            }}
          >

            {/* MOBILE MENU */}
            {isMobile && (
              <button
                onClick={() =>
                  setSidebarOpen(!isSidebarOpen)
                }
                style={styles.menuBtn}
                aria-label="Open menu"
              >
                {isSidebarOpen ? (
                  <X size={20} />
                ) : (
                  <Menu size={20} />
                )}
              </button>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                minWidth: 0,
                flex: 1,
                overflow: "hidden",
              }}
            >
              {!isMobile && (
                <div className="vanya-hide-on-mobile" style={styles.breadcrumb}>
                  <span>Vanya Collections</span>
                  <span style={{ opacity: 0.4 }}>/</span>
                  <span style={{ color: "#183B32", fontWeight: "600" }}>{getPageTitle()}</span>
                </div>
              )}
              <div
                className="vanya-header-title"
                style={{
                  ...styles.headerTitle,
                  fontSize: isMobile ? "15px" : "18px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {getPageTitle()}
              </div>
            </div>

          </div>

          {/* RIGHT SIDE */}
          <div
            style={{
              ...styles.headerRight,
              flexShrink: 0,
              gap: isMobile ? "8px" : "16px",
            }}
          >

            {!isMobile && (
              <div className="vanya-hide-on-mobile" style={styles.storeStatusBadge}>
                <span style={styles.statusDot} />
                <span>Online Store Active</span>
              </div>
            )}

            <div
              style={{
                ...styles.adminInfo,
                padding: isMobile ? "0" : "4px 8px 4px 4px",
                backgroundColor: isMobile ? "transparent" : "#f8fafc",
                border: isMobile ? "none" : "1px solid #e2e8f0",
              }}
            >

              <div
                style={{
                  ...styles.adminAvatar,
                  width: isMobile ? "34px" : "36px",
                  height: isMobile ? "34px" : "36px",
                  fontSize: isMobile ? "14px" : "15px",
                }}
                title="Administrator"
              >
                V
              </div>

              {!isMobile && (
                <div className="vanya-hide-on-mobile" style={styles.adminText}>
                  <span style={styles.adminName}>
                    Administrator
                  </span>

                  <span style={styles.adminRole}>
                    Super Admin
                  </span>
                </div>
              )}

            </div>

          </div>

        </header>

        {/* =====================================================
            PAGE CONTENT
        ===================================================== */}
        <div
          style={{
            ...styles.content,
            padding: isMobile ? "14px 12px 36px" : "28px",
          }}
        >
          <Outlet />
        </div>

      </main>

    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const styles = {

  // MAIN WRAPPER
  adminWrapper: {
    display: "flex",
    minHeight: "100vh",
    backgroundColor: "#f7f7f5",
    fontFamily:
      "'Inter', 'Segoe UI', Arial, sans-serif",
  },

  // OVERLAY
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    zIndex: 999,
    backdropFilter: "blur(2px)",
  },

  // SIDEBAR
  sidebar: {
    position: "fixed",
    top: 0,
    bottom: 0,
    width: "260px",
    backgroundColor: "#183B32",
    color: "#ffffff",
    zIndex: 1000,
    transition:
      "left 0.28s ease",
    display: "flex",
    flexDirection: "column",
    boxShadow:
      "4px 0 20px rgba(0,0,0,0.12)",
    overflowY: "auto",
  },

  // LOGO SECTION
  logoSection: {
    minHeight: "90px",
    padding: "0 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  logoImgWrapper: {
    width: "58px",
    height: "58px",
    borderRadius: "50%",
    backgroundColor: "#0d251e",
    border: "1.5px solid #D4AF37",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
    overflow: "hidden",
  },

  logoImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scale(1.08)",
  },

  logo: {
    fontSize: "17px",
    fontWeight: "800",
    letterSpacing: "1px",
    color: "#D4AF37",
    lineHeight: "22px",
    whiteSpace: "nowrap",
    textShadow: "0 2px 4px rgba(0,0,0,0.2)",
  },

  logoSub: {
    fontSize: "9px",
    letterSpacing: "2.5px",
    color: "rgba(255,255,255,0.75)",
    marginTop: "2px",
    fontWeight: "700",
  },

  closeBtn: {
    marginLeft: "auto",
    background: "transparent",
    border: "none",
    color: "#ffffff",
    cursor: "pointer",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  divider: {
    height: "1px",
    backgroundColor:
      "rgba(255,255,255,0.10)",
    margin: "0 18px",
  },

  // NAVIGATION
  nav: {
    padding: "20px 12px",
    flex: 1,
  },

  menuLabel: {
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "1.5px",
    color: "rgba(255,255,255,0.42)",
    padding: "0 12px 10px",
  },

  navItem: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: "100%",
    minHeight: "48px",
    marginBottom: "5px",
    padding: "0 13px",
    boxSizing: "border-box",
    borderRadius: "9px",
    color: "rgba(255,255,255,0.78)",
    textDecoration: "none",
    fontSize: "13px",
    fontWeight: "500",
    transition:
      "all 0.2s ease",
  },

  activeNavItem: {
    backgroundColor:
      "rgba(212,175,55,0.14)",
    color: "#ffffff",
    fontWeight: "600",
  },

  activeIndicator: {
    position: "absolute",
    left: 0,
    top: "9px",
    bottom: "9px",
    width: "3px",
    borderRadius: "0 4px 4px 0",
    backgroundColor: "#D4AF37",
  },

  navIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "rgba(255,255,255,0.65)",
    flexShrink: 0,
  },

  activeNavIcon: {
    backgroundColor: "#D4AF37",
    color: "#183B32",
  },

  navText: {
    marginLeft: "8px",
    whiteSpace: "nowrap",
  },

  arrow: {
    marginLeft: "auto",
    color: "#D4AF37",
  },

  // SIDEBAR BOTTOM
  sidebarBottom: {
    padding: "0 12px 18px",
  },

  bottomDivider: {
    height: "1px",
    backgroundColor:
      "rgba(255,255,255,0.10)",
    margin: "0 6px 12px",
  },

  logoutBtn: {
    width: "100%",
    minHeight: "46px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "0 13px",
    background: "transparent",
    border: "none",
    borderRadius: "9px",
    color: "rgba(255,255,255,0.75)",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
    textAlign: "left",
  },

  logoutIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  version: {
    textAlign: "center",
    marginTop: "12px",
    fontSize: "9px",
    color: "rgba(255,255,255,0.3)",
    letterSpacing: "0.5px",
  },

  // MAIN
  main: {
    flex: 1,
    minWidth: 0,
    transition:
      "margin-left 0.28s ease",
  },

  // HEADER
  topHeader: {
    height: "64px",
    backgroundColor: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 25px",
    borderBottom:
      "1px solid #eeeeea",
    boxSizing: "border-box",
    position: "sticky",
    top: 0,
    zIndex: 900,
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },

  menuBtn: {
    width: "40px",
    height: "40px",
    backgroundColor: "#f5f5f2",
    border: "1px solid #e5e5df",
    borderRadius: "9px",
    color: "#183B32",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    color: "#64748b",
    fontWeight: "500",
    letterSpacing: "0.2px",
  },

  headerTitle: {
    fontSize: "18px",
    fontWeight: "800",
    color: "#183B32",
    letterSpacing: "-0.2px",
  },

  // ADMIN INFO
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  storeStatusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    padding: "5px 12px",
    borderRadius: "20px",
    backgroundColor: "#ecfdf5",
    border: "1px solid #a7f3d0",
    color: "#065f46",
    fontSize: "12px",
    fontWeight: "600",
  },

  statusDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: "#10b981",
    boxShadow: "0 0 0 2px rgba(16, 185, 129, 0.2)",
  },

  adminInfo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "4px 8px 4px 4px",
    borderRadius: "30px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
  },

  adminAvatar: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #f5d470 0%, #d4af37 100%)",
    color: "#183B32",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "800",
    fontSize: "15px",
    boxShadow: "0 2px 6px rgba(212, 175, 55, 0.3)",
    border: "2px solid #ffffff",
  },

  adminText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  adminName: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#183B32",
  },

  adminRole: {
    fontSize: "10px",
    color: "#999999",
  },

  // CONTENT
  content: {
    padding: "28px",
    boxSizing: "border-box",
  },
};

export default VanyaLayout;